import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormControl } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { delay, map, startWith, switchMap, withLatestFrom } from 'rxjs/operators';
import {
  getInitialsFromTeamMember,
  getTeamMemberName,
  getVisibleToTeamMembersText,
  TeamMember,
} from '@shared';
import { SharedResourcesAutocompleteService } from '@core/shared-resources/services/shared-resources-autocomplete.service';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { AvatarBackgroundSizes, AvatarShapes } from 'mojo-avatar';
import { Store } from '@ngrx/store';
import { sharedResourcesUpdateCurrent } from '@core/shared-resources/store/shared-resources.actions';
import { getCurrentSharedResourceTeamMembers } from '@core/shared-resources/store/shared-resources.selectors';
import { getUsersLoggedIn } from '@collections/users';

export interface NotesTeamMembersModalData {
  headerTitle: string;
  templateAppearance: string;
  autocompletePlaceholder: string;
  inputsThemeColor: string;
}

@Component({
  selector: 'mojo-notes-team-members-modal',
  templateUrl: './notes-team-members-modal.component.html',
  styleUrls: ['./notes-team-members-modal.component.scss'],
})
export class NotesTeamMembersModalComponent implements OnInit, OnDestroy {
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;

  headerTitle: string;
  autocompletePlaceholder: string;
  templateAppearance: string;
  inputsThemeColor: string;

  avatarPhotoShape = AvatarShapes.circle;
  avatarBackground = AvatarBackgroundSizes.cover;

  searchControl: UntypedFormControl = new UntypedFormControl('');
  selectedTeamMembers: TeamMember[] = [];
  teamMembersSearchResult: Observable<TeamMember[]>;

  subscription = new Subscription();

  loggedInUserId$ = this.store.select(getUsersLoggedIn).pipe(map((u) => u?.id));

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: NotesTeamMembersModalData,
    private dialogRef: MatDialogRef<NotesTeamMembersModalComponent>,
    private autocompleteService: SharedResourcesAutocompleteService,
    private store: Store,
  ) {
    this.headerTitle = this.data.headerTitle;
    this.autocompletePlaceholder = this.data.autocompletePlaceholder;
    this.templateAppearance = this.data.templateAppearance;
    this.inputsThemeColor = this.data.inputsThemeColor;
    this.teamMembersSearchResult = this.searchControl.valueChanges.pipe(
      startWith(''),
      delay(200),
      switchMap((value) => this.searchTeamMembers(value)),
    );
  }

  ngOnInit() {
    this.subscription.add(
      this.store
        .select(getCurrentSharedResourceTeamMembers)
        .subscribe((currentTeamMembers) => (this.selectedTeamMembers = currentTeamMembers)),
    );
  }

  searchTeamMembers(value): Observable<TeamMember[]> {
    return this.autocompleteService.search(value).pipe(
      withLatestFrom(this.loggedInUserId$),
      map(([teamMembers, loggedInId]) =>
        teamMembers.filter(
          (teamMember) =>
            teamMember.id !== loggedInId &&
            teamMember.name.toLowerCase().includes(value.toLowerCase()) &&
            !this.selectedTeamMembers.some((selectedOption) => selectedOption.id === teamMember.id),
        ),
      ),
    );
  }

  onTeamMemberSelected(event: any): void {
    const option: TeamMember = event.option.value;
    this.selectedTeamMembers.push(option);
    this.searchControl.setValue('');
    this.autocompleteTrigger?.closePanel();
  }

  removeTeamMember(toRemove: TeamMember): void {
    const index = this.selectedTeamMembers.findIndex(
      (selectedOption) => selectedOption.id === toRemove.id,
    );
    if (index !== -1) {
      this.selectedTeamMembers.splice(index, 1);
    }
  }

  openAutocompletePanel(): void {
    if (!this.autocompleteTrigger.panelOpen) {
      this.autocompleteTrigger.openPanel();
    }
  }

  onSave() {
    this.store.dispatch(sharedResourcesUpdateCurrent({ teamMembers: this.selectedTeamMembers }));
    if (this.selectedTeamMembers && this.selectedTeamMembers.length > 0) {
      this.dialogRef.close(getVisibleToTeamMembersText(this.selectedTeamMembers));
    } else {
      this.dialogRef.close();
    }
    this.searchControl.setValue('');
    this.selectedTeamMembers = [];
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  protected readonly getInitials = getInitialsFromTeamMember;
  protected readonly getName = getTeamMemberName;
}
